import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, MapPinIcon, HeartIcon, HistoryIcon, GraduationCapIcon, GemIcon, HotelIcon, TrendingUpIcon, ArrowRightIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-348">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-349">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f5f0e8] mb-6 tracking-tight" data-id="element-350">
      {title} <span className="text-[#d4a017]" data-id="element-351">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-352"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-353">
        {description}
      </p>}
  </motion.div>;
const partnerCard = (icon: React.ReactNode, title: string, body: string, delay: number) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.6,
  delay
}} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-7" data-id="element-354">
    <div className="text-[#d4a017]/30 mb-4" data-id="element-355">{icon}</div>
    <h4 className="font-serif text-lg text-[#f5f0e8] mb-3" data-id="element-356">{title}</h4>
    <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-357">
      {body}
    </p>
  </motion.div>;
/* ─── SPONSORS ─── */
export function SponsorsSection() {
  return <section id="sponsors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-358">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-359"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-360">
        {sectionHeader('Section 11', 'Sponsor', 'Opportunities', 'Premium brand alignment with heritage, excellence, and multicultural innovation.')}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-361">
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-362" />, 'Hosted Tables', 'Private branded tables for 10 guests with premium hospitality, dedicated service, and branded photo moments throughout the evening.', 0)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-363" />, 'Venue Banners', 'Premium banner placement throughout the venue — entrance corridor, main room, and exit experience. High-visibility, high-impact.', 0.05)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-364" />, 'Founder Wall', 'Permanent recognition on the Founder Wall — a digital and physical display honoring the partners who made this experience possible.', 0.1)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-365" />, 'Scene Sponsorship', 'Sponsor a specific portal or scene — your brand woven into the narrative itself. The most immersive form of brand integration.', 0.15)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-366" />, 'Premium Hospitality', 'VIP reception hosting, private chef interaction, behind-the-scenes tours, and exclusive access to the creative team.', 0.2)}
          {partnerCard(<Building2Icon className="w-7 h-7" data-id="element-367" />, 'Donor Recognition', 'Tax-deductible charitable contribution with full recognition across event materials, press releases, and community communications.', 0.25)}
        </div>

        {/* Sponsors & Patronage Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-368">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-369">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-370">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/bJZtX1yNRXk" title="Sponsors & Patronage — Life in the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-371"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-372">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-373">
                Sponsors &amp; Patronage
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-374">
                How sponsorship and patronage bring this immersive cultural
                experience to life.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── ONTARIO TOURISM ─── */
export function OntarioTourismSection() {
  return <section id="ontario-tourism" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-375">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-376">
        {sectionHeader('Section 12', 'Ontario', 'Tourism', 'A cultural tourism asset that positions Toronto as a global destination for immersive heritage experiences.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-377">
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-378" />, 'Tourism Relevance', 'Life in the Sikh Kingdoms is a destination experience — drawing visitors from across Ontario, Canada, and internationally. It combines culture, cuisine, and technology in a format that appeals to the modern cultural tourist.', 0)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-379" />, 'Cultural Significance', "This experience celebrates one of Canada's most vibrant diaspora communities. It positions Ontario as a leader in multicultural arts and immersive cultural experiences — a story the province can proudly tell.", 0.1)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-380" />, 'Education & Community', 'School field trips, community nights, and youth programs create lasting educational value. This is not just entertainment — it is a living classroom that connects generations to heritage.', 0.2)}
          {partnerCard(<MapPinIcon className="w-7 h-7" data-id="element-381" />, 'Premium Event Potential', "Corporate events, diplomatic receptions, and private experiences generate premium tourism revenue. Each event brings visitors to Toronto's entertainment district, supporting local hotels, restaurants, and businesses.", 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── GURDWARAS ─── */
export function GurdwarasSection() {
  return <section id="gurdwaras" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-382">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-383"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-384">
        {sectionHeader('Section 13', 'Gurdwaras &', 'Community', 'Cultural preservation, youth engagement, and heritage celebration — rooted in community.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-385">
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-386" />, 'Cultural & Community Value', 'This experience brings Sikh history to life in a way that resonates with all generations — from elders who remember the stories to youth discovering their heritage for the first time. It is a bridge between past and future.', 0)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-387" />, 'Youth Engagement', 'Interactive technology, immersive storytelling, and sensory design speak the language of the next generation. This is heritage preservation through innovation — meeting young people where they are.', 0.1)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-388" />, 'Hosted Community Nights', 'Gurdwaras can host dedicated community nights — group tickets, youth programs, and elder appreciation evenings. A meaningful outing that celebrates shared heritage.', 0.2)}
          {partnerCard(<HeartIcon className="w-7 h-7" data-id="element-389" />, 'Support Opportunities', 'Community organizations can support the experience through group ticket purchases, volunteer coordination, outreach, and partnership with the ROM Sikh Art and Culture Gallery fund.', 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── PREVIOUS SPONSORS ─── */
export function PreviousSponsorsSection() {
  return <section id="previous-sponsors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-390">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-391">
        {sectionHeader('Section 14', 'Previous', 'Sponsors', 'The next chapter of a legacy — from Art of the Sikh Kingdoms to Life in the Sikh Kingdoms.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-392">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-393">
            In August 2000, the Royal Ontario Museum hosted{' '}
            <strong className="text-[#d4a017]" data-id="element-394">
              "Art of the Sikh Kingdoms"
            </strong>{' '}
            — the second most popular exhibition in ROM history. That exhibition
            was made possible by visionary sponsors who believed in the power of
            Sikh heritage to captivate a global audience.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-395">
            Life in the Sikh Kingdoms is the next chapter of that legacy. It
            takes the same heritage and brings it into the immersive age — 360°
            projections, interactive technology, royal cuisine, and
            multi-sensory storytelling.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-396">
            Previous sponsors of Art of the Sikh Kingdoms are natural partners
            for this experience. The continuity from past cultural support into
            an immersive future is a powerful story — and one that resonates
            with communities, media, and institutions alike.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/20 bg-[#d4a017]/5 p-8 text-center" data-id="element-397">
          <p className="text-[#d4a017] italic font-light text-lg leading-relaxed" data-id="element-398">
            "The sponsors who made Art of the Sikh Kingdoms possible changed how
            the world sees Sikh heritage. Now, a new generation of partners can
            take that legacy further than ever before."
          </p>
        </motion.div>

        {/* Previous Supporters Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-399">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-400">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-401">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/utjPWynQXrw" title="Previous Supporters of Art of the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-402"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-403">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-404">
                Art of the Sikh Kingdoms — Legacy &amp; Continuity
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-405">
                Why previous supporters of Art of the Sikh Kingdoms are natural
                partners for this next chapter.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── SCHOOLS ─── */
export function SchoolsSection() {
  return <section id="schools" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-406">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-407"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-408">
        {sectionHeader('Section 15', 'Schools &', 'Education', 'A living classroom where history is not read — it is experienced.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-409">
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-410" />, 'Educational Value', 'Students engage with 200+ years of Sikh history through immersive technology, sensory storytelling, and interactive experiences. This is experiential learning at its most powerful — history that students feel, not just study.', 0)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-411" />, 'Learning Outcomes', 'Aligned with Ontario curriculum standards for history, social studies, and multicultural education. Students explore themes of leadership, pluralism, innovation, and resilience through the lens of the Sikh Empire.', 0.1)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-412" />, 'School Field Trip Version', 'A dedicated daytime format designed for school groups — shorter duration, educator guide, pre-visit materials, and post-visit discussion prompts. Talkback sessions with historians and creators available.', 0.2)}
          {partnerCard(<GraduationCapIcon className="w-7 h-7" data-id="element-413" />, 'Cultural Learning', 'Students from all backgrounds discover the richness of Sikh heritage — its art, architecture, cuisine, music, and values. A powerful tool for building empathy, understanding, and multicultural appreciation.', 0.3)}
        </div>
      </div>
    </section>;
}
/* ─── WEDDING PLANNERS ─── */
export function WeddingPlannersSection() {
  return <section id="wedding-planners" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-414">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-415">
        {sectionHeader('Section 16', 'Wedding', 'Planners', 'Immersive storyworlds for the most important celebration of a lifetime.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-416">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-417">
            The immersive technology and storytelling framework behind Life in
            the Sikh Kingdoms can be adapted for luxury weddings. Imagine a
            sangeet where the walls come alive with the couple's story, a
            reception where the room transforms for the grand reveal, or a
            welcome night that transports guests to another world entirely.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-418">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-419">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-420" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-421">
              Welcome Night
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-422">
              Immersive arrival experience
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-423">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-424" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-425">Sangeet</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-426">
              360° story of the couple
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-427">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-428" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-429">
              Reception Reveal
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-430">
              Grand entrance transformation
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 text-center" data-id="element-431">
            <GemIcon className="w-6 h-6 text-[#d4a017]/40 mx-auto mb-3" data-id="element-432" />
            <p className="text-[#f5f0e8] text-sm font-medium mb-1" data-id="element-433">
              Luxury Upsell
            </p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-434">
              Premium immersive add-on
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
/* ─── RESORT SALES ─── */
export function ResortSalesSection() {
  return <section id="resort-sales" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-435">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/8 to-transparent" data-id="element-436"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-437">
        {sectionHeader('Section 17', 'Resort', 'Sales', 'The Ever After Experience — a premium upsell for resorts and luxury venues.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-438">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-439">
            Resorts and luxury venues can offer the immersive experience as a
            premium upsell — a "Signature Weekend" that transforms a standard
            stay into an unforgettable cultural journey. Centralized creative
            direction with local execution means consistent quality at any
            venue.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-440">
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-441">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-442">
                Welcome
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-443">
                Immersive arrival
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-444">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-445">
                Sangeet
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-446">
                Cultural evening
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-447">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-448">
                Reveal
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-449">
                Grand moment
              </p>
            </div>
            <div className="border border-[#d4a017]/10 p-4 text-center" data-id="element-450">
              <p className="text-[#d4a017]/60 text-xs tracking-wider uppercase mb-1" data-id="element-451">
                Weekend
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-452">
                Signature package
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── INVESTORS ─── */
export function InvestorsSection() {
  return <section id="investors" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-453">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-454">
        {sectionHeader('Section 18', 'Investors &', 'Family Offices', 'More than a show — a scalable immersive platform with multiple revenue streams.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-455">
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-456">
            Life in the Sikh Kingdoms is not a one-time event — it is a
            category-creating platform. The immersive dining and cultural
            experience format is scalable, licensable, and applicable across
            multiple verticals: tourism, weddings, private events, corporate
            experiences, and cultural licensing.
          </p>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-457">
            For investors and family offices, this represents an opportunity to
            participate in the creation of a new category — immersive cultural
            experiences that combine heritage, technology, cuisine, and
            storytelling into a premium, repeatable format with global tour
            potential.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-458">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-459">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-460">Tourism</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-461">
              Destination experiences
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-462">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-463">Weddings</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-464">
              Luxury immersive events
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-465">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-466">Private</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-467">
              Corporate & VIP events
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 text-center" data-id="element-468">
            <p className="text-[#d4a017] font-serif text-2xl mb-1" data-id="element-469">Licensing</p>
            <p className="text-[#f5f0e8]/30 text-xs font-light" data-id="element-470">
              Global cultural IP
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}